/**
 * PSI Knowledge Profile — the Agents tab body.
 *
 * Replaces the old POM stats widgets with a single page that measures what
 * PSI has learned about THIS organization. Auto-extending: every distinct
 * (agent_id, field) pair in agent_learning_events shows up as its own row,
 * grouped under its agent. New agents appear with zero code change.
 *
 * Layout:
 *   1. Headline       — total learnings + agents + last-at
 *   2. Knowledge      — grouped by agent, one row per (agent, field) pair,
 *                       bar + sparkline + count, click to drill in
 *   3. Recent         — chronological feed of every correction
 *   4. Agents         — rollup of which agents have learned
 *   5. CSV export     — compliance-only, SOC 2 evidence
 *
 * Visual contract: calm. Slate palette, thin borders, monospace for
 * technical metadata, no emoji, no gradients, no AI personification.
 * SOC 2: all queries org-scoped, CSV export gated to compliance roles.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

// Small allowlist of abbreviations we want to keep upper-cased instead of
// title-cased ("POM Brain" not "Pom Brain"). Everything else is plain Title.
const UPPER_TOKENS = new Set(['POM', 'CAP', 'FDR', 'NA', 'AI', 'PPM', 'COA', 'CIL', 'CMS', 'PRIQ', 'API']);

function humanize(raw) {
  if (!raw) return '';
  return String(raw)
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => {
      const up = w.toUpperCase();
      if (UPPER_TOKENS.has(up)) return up;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

function agentDisplayName(agent_id, agent_label) {
  if (agent_label && agent_label !== agent_id) return agent_label;
  return humanize(agent_id);
}

// ---------------------------------------------------------------------------
// Sparkline: filled area under a line, calm slate colors
// ---------------------------------------------------------------------------
function Sparkline({ data, w = 72, h = 18 }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const max = Math.max(1, ...data);
  const step = w / Math.max(1, data.length - 1);
  const linePoints = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(' ');
  const areaPoints = `0,${h} ${linePoints} ${w},${h}`;
  return (
    <svg width={w} height={h} aria-hidden="true" style={{ display: 'block' }}>
      <polygon points={areaPoints} fill="#f1f5f9" />
      <polyline points={linePoints} fill="none" stroke="#475569" strokeWidth="1" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Knowledge row inside an agent group
// ---------------------------------------------------------------------------
function DomainRow({ d, onOpen }) {
  const pct = Math.min(100, d.count * 5); // each correction fills 5% of the bar; saturates at 20
  return (
    <button
      type="button"
      onClick={() => onOpen(d)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr 88px 80px 18px',
        gap: 14,
        alignItems: 'center',
        width: '100%',
        padding: '10px 14px',
        background: '#ffffff',
        border: '0',
        borderTop: '1px solid #f1f5f9',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'background 100ms ease',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#fafbfc'}
      onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
    >
      <div>
        <div style={{ fontSize: 13.5, color: '#0f172a' }}>{d.field_label}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>
          {d.last_at ? d.last_ago : 'no learnings'}
        </div>
      </div>
      <div style={{ position: 'relative', height: 4, background: '#f1f5f9', borderRadius: 999 }}>
        <div
          style={{
            position: 'absolute', top: 0, left: 0, height: '100%',
            width: `${pct}%`, background: '#1e293b', borderRadius: 999,
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: '#475569', fontFamily: 'ui-monospace, monospace', textAlign: 'right' }}>
        {fmt(d.count)} {d.count === 1 ? 'learning' : 'learnings'}
      </div>
      <Sparkline data={d.sparkline_30d} />
      <span style={{ color: '#cbd5e1', fontSize: 14, textAlign: 'right', fontFamily: 'ui-monospace, monospace' }} aria-hidden="true">›</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Agent group (header + rows)
// ---------------------------------------------------------------------------
function AgentGroup({ agentName, agentId, rows, total, onOpen }) {
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          padding: '12px 14px',
          borderBottom: '1px solid #e2e8f0',
          background: '#fafbfc',
        }}
      >
        <div>
          <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{agentName}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace', marginTop: 2 }}>
            {agentId}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#475569', fontFamily: 'ui-monospace, monospace' }}>
          {fmt(total)} {total === 1 ? 'learning' : 'learnings'}
        </div>
      </div>
      <div>
        {rows.map(d => <DomainRow key={d.key} d={d} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drill-down drawer
// ---------------------------------------------------------------------------
function DomainDrawer({ domain, onClose }) {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/agents/knowledge-profile/domain/${encodeURIComponent(domain.key)}`, { credentials: 'include' });
        const data = await r.json();
        if (!cancel) {
          if (data.success) setRules(data.rules || []);
          else setError(data.message || 'Could not load.');
        }
      } catch { if (!cancel) setError('Connection error.'); }
    })();
    return () => { cancel = true; };
  }, [domain.key]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.20)', zIndex: 55 }}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-label={`${domain.field_label} learnings`}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 92vw)',
          background: '#ffffff', borderLeft: '1px solid #cbd5e1', zIndex: 60,
          overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ position: 'sticky', top: 0, background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
              {agentDisplayName(domain.agent_id, domain.agent_label)}
            </div>
            <div style={{ fontSize: 17, color: '#0f172a', fontWeight: 600, marginTop: 4 }}>{domain.field_label}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '4px 10px', fontSize: 12, color: '#475569', cursor: 'pointer', borderRadius: 6 }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: '8px 20px 32px' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: 13, padding: '12px 0' }}>{error}</div>}
          {!rules && !error && <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>Loading…</div>}
          {rules && rules.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>No corrections recorded in this area yet.</div>
          )}
          {rules && rules.map(r => (
            <div key={r.id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 13.5, color: '#0f172a', lineHeight: 1.5 }}>
                {r.original ? (
                  <>
                    <span style={{ color: '#94a3b8', textDecoration: 'line-through', marginRight: 8 }}>{r.original}</span>
                    <span style={{ color: '#64748b' }}>→</span>
                    <span style={{ marginLeft: 8, fontWeight: 600 }}>{r.corrected}</span>
                  </>
                ) : (
                  <span style={{ fontWeight: 600 }}>{r.corrected}</span>
                )}
              </div>
              {r.reason && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.55 }}>“{r.reason}”</div>
              )}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, fontFamily: 'ui-monospace, monospace' }}>
                {r.ago}  ·  {r.source_app}  ·  {r.actor_email || 'system'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Timeline row (Recent learnings feed)
// ---------------------------------------------------------------------------
function TimelineRow({ ev }) {
  const fieldHumanized = humanize(ev.field);
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#0f172a' }}>
          <span style={{ fontWeight: 600 }}>{agentDisplayName(ev.agent_id, ev.agent_label)}</span>
          <span style={{ color: '#cbd5e1', margin: '0 8px' }}>·</span>
          <span style={{ color: '#64748b' }}>{fieldHumanized}</span>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>
          {ev.ago}
        </div>
      </div>
      <div style={{ fontSize: 13.5, color: '#0f172a', marginTop: 6, lineHeight: 1.5 }}>
        {ev.original ? (
          <>
            <span style={{ color: '#94a3b8', textDecoration: 'line-through', marginRight: 8 }}>{ev.original}</span>
            <span style={{ color: '#64748b' }}>→</span>
            <span style={{ marginLeft: 8, fontWeight: 600 }}>{ev.corrected}</span>
          </>
        ) : (
          <span style={{ fontWeight: 600 }}>{ev.corrected}</span>
        )}
      </div>
      {ev.reason && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.55 }}>“{ev.reason}”</div>
      )}
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, fontFamily: 'ui-monospace, monospace' }}>
        {ev.source_app}  ·  {ev.actor_email || 'system'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------
export default function AgentDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`${API}/api/agents/knowledge-profile`, { credentials: 'include' });
      const j = await r.json();
      if (j.success) setData(j);
      else setError(j.message || 'Could not load the knowledge profile.');
    } catch {
      setError('Connection error. Please refresh.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group domains by agent (server already returns them ordered agent->desc count)
  const grouped = useMemo(() => {
    if (!data) return [];
    const groups = new Map();
    for (const d of (data.domains || [])) {
      const key = d.agent_id;
      if (!groups.has(key)) {
        groups.set(key, {
          agentId:   d.agent_id,
          agentName: agentDisplayName(d.agent_id, d.agent_label),
          rows:      [],
          total:     0,
        });
      }
      const g = groups.get(key);
      g.rows.push(d);
      g.total += d.count;
    }
    return Array.from(groups.values()).sort((a, b) => b.total - a.total);
  }, [data]);

  if (loading) return <div style={{ padding: 32, color: '#94a3b8', fontSize: 13 }}>Loading…</div>;
  if (error)   return <div style={{ padding: 32, color: '#b91c1c', fontSize: 13 }}>{error}</div>;
  if (!data)   return null;

  const hasLearnings = (data.totals?.events || 0) > 0;

  return (
    <div style={{ padding: '8px 4px 32px', color: '#0f172a' }}>
      {/* Headline */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
            PSI Knowledge
          </div>
          <div style={{ marginTop: 6, fontSize: 30, color: '#0f172a', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
            {fmt(data.totals.events)} {data.totals.events === 1 ? 'learning' : 'learnings'}
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
            {hasLearnings
              ? <>across <strong style={{ color: '#0f172a', fontWeight: 600 }}>{fmt(data.totals.agents)}</strong> {data.totals.agents === 1 ? 'agent' : 'agents'} in your organization</>
              : <>Corrections you make in PSI, Mandate, or any other app will appear here.</>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
            Last learning
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>
            {data.totals.last_event_at ? data.totals.last_ago : '—'}
          </div>
          <a
            href={`${API}/api/agents/knowledge-profile/export.csv`}
            style={{
              marginTop: 10, display: 'inline-block',
              fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: '#475569', textDecoration: 'none',
              border: '1px solid #cbd5e1', padding: '4px 10px',
              borderRadius: 6, fontFamily: 'ui-monospace, monospace',
            }}
            title="SOC 2 evidence export — every learning event for this organization"
          >
            Export CSV
          </a>
        </div>
      </div>

      {/* Knowledge — grouped by agent */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 10, fontFamily: 'ui-monospace, monospace' }}>
          Knowledge
        </div>
        {grouped.length === 0 && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px 24px', color: '#94a3b8', fontSize: 13 }}>
            PSI has no recorded learnings for this organization yet.
          </div>
        )}
        {grouped.map(g => (
          <AgentGroup
            key={g.agentId}
            agentId={g.agentId}
            agentName={g.agentName}
            rows={g.rows}
            total={g.total}
            onOpen={setDrawer}
          />
        ))}
      </div>

      {/* Two columns: timeline + per-agent rollup */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 10, fontFamily: 'ui-monospace, monospace' }}>
            Recent learnings
          </div>
          <div style={{ border: '1px solid #e2e8f0', background: '#ffffff', borderRadius: 10, padding: '4px 18px' }}>
            {(data.timeline || []).length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '14px 0' }}>No corrections yet.</div>
            )}
            {(data.timeline || []).map(ev => <TimelineRow key={ev.id} ev={ev} />)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 10, fontFamily: 'ui-monospace, monospace' }}>
            Agents
          </div>
          <div style={{ border: '1px solid #e2e8f0', background: '#ffffff', borderRadius: 10, padding: '4px 18px' }}>
            {(data.agents || []).length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '14px 0' }}>No agent activity yet.</div>
            )}
            {(data.agents || []).map(a => (
              <div key={a.agent_id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                    {agentDisplayName(a.agent_id, a.agent_label)}
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', fontFamily: 'ui-monospace, monospace' }}>
                    {fmt(a.count)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, fontFamily: 'ui-monospace, monospace' }}>
                  {a.last_at ? a.last_ago : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {drawer && <DomainDrawer domain={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}
